/**
 * RCO-1 — canonical helper for "which uploaded docs actually got cited
 * in this report's narrative?".
 *
 * The "Documents informing this report" appendix used to read
 * `ReportRef.userIncluded`, which is an INTENT stamp set at rerun-
 * wizard time (the user toggled the doc on). That conflates "I told
 * the model to look at this doc" with "the narrative actually leaned
 * on it" — a Redhead-Feasibility report would headline "81 included"
 * while citing maybe 30 of those 81. This helper produces the FACT
 * set so the appendix can split "cited" from "provided but not cited"
 * and the headline can reflect what the narrative really leaned on.
 *
 * Two independent ground-truth signals are unioned:
 *
 *   Path A — `[Ref N]` body tokens resolved through SED lineage.
 *     Walk every section's narrative text with the shared numbered-token
 *     parser (including reliability labels, colon and grouped forms),
 *     collect the 1-based indices, look each one up in
 *     `report.references[idx-1]`, and resolve the resulting
 *     `DataSource` to a docId through `resolveDocumentLineage` (which
 *     reads `sed[ref.field].linkedDocumentRefs[*].docId`). Only
 *     SED-section refs with `pageNumber` resolve — external URL refs,
 *     ADAMS rows, knowledge-base entries, etc. correctly drop out
 *     because they aren't backed by an uploaded doc anyway.
 *
 *   Path B — `ReportClaim.sources[]` carrying a `doc:<docId>` prefix.
 *     CT6 schema bump introduced structured `claims[]` where each
 *     sentence carries lineage-target refs. When a claim source uses
 *     the `doc:` prefix (`parseClaimSource` returns prefix === 'doc:'),
 *     the payload is the docId verbatim — no SED traversal needed.
 *
 *   Path C — `(per "filename")` inline citations (R-74 / A2).
 *     Walk the narrative blob for `/\(per\s+"([^"]+)"\)/gi` and resolve
 *     each filename against live `SiteDocument[]` via fuzzy match.
 *
 * The union covers all citation styles the codebase currently emits.
 * If a report uses none, the returned set is empty — which is the
 * correct user message ("no citation telemetry available for this
 * report") instead of overpromising.
 *
 * Pure projection — no I/O, never mutates `report` / `sed`, never
 * throws. The walker is O(N) over narrative length; for the largest
 * reports observed in prod (~80 sections × ~5 KB) it lands in single-
 * digit ms. Wrap in `useMemo` keyed on `report` / `sed` identity at
 * the React caller.
 *
 * Composability:
 *   - DRC pack (sibling, 0.99218) is adding `computeReportDocStats`.
 *     Extending that helper to consume this set is RCO-3 and lives
 *     behind the DRC ship.
 *   - UPE pack (0.99214) added `SiteDocument.extractionStatus`; this
 *     helper is orthogonal — UPE answers "did extraction succeed?",
 *     RCO answers "did the narrative cite it?". Both compose into the
 *     "did this doc both extract correctly AND end up cited?" admin
 *     surface (deferred).
 */
import type { SiteRagReport, SiteDocument } from '../types/siteRag';
import type { SiteEvidenceDocument } from '../types/siteEvidence';
import { resolveReferencedDocumentId } from './resolveDocumentLineage';
import { parseClaimSource } from '../types/citation';
import { getSectionsForType, type SectionDef } from '../config/reportSections';
import { extractNumberedRefIds } from './citationTokens';
import { resolveSiteDocumentFileName } from './site/resolveSiteDocuments';

export const PER_FILENAME_RE = /\(per\s+"([^"]+)"\)/gi;

export function matchDocByFilename(fn: string, liveDocs: SiteDocument[]): SiteDocument | null {
  if (fn.length < 4) return null;
  const fnLower = fn.toLowerCase();
  const exact = liveDocs.find((d, idx) => resolveSiteDocumentFileName(d, `doc-${idx}`).toLowerCase() === fnLower);
  if (exact) return exact;
  const contains = liveDocs.find((d, idx) => resolveSiteDocumentFileName(d, `doc-${idx}`).toLowerCase().includes(fnLower));
  if (contains) return contains;
  const reverse = liveDocs.find((d, idx) => {
    const docName = resolveSiteDocumentFileName(d, `doc-${idx}`).toLowerCase();
    return docName.length >= 4 && fnLower.includes(docName);
  });
  return reverse ?? null;
}

/**
 * Returns the set of docIds whose narrative was actually cited in
 * the body — via `[Ref N]` (Path A), `ReportClaim.sources` doc:<id>
 * (Path B), or `(per "filename")` (Path C). Empty set when no signal fires.
 */
export function computeBodyCitedDocIds(
  report: SiteRagReport,
  sed: SiteEvidenceDocument | undefined,
  opts: { liveDocs?: SiteDocument[] } = {},
): Set<string> {
  const out = new Set<string>();
  const liveDocs = opts.liveDocs ?? report.documents ?? [];

  // Path A — [Ref N] tokens resolved through SED lineage.
  if (sed && Array.isArray(report.references) && report.references.length > 0) {
    const narrativeBlob = collectNarrativeBlob(report);
    if (narrativeBlob.length > 0) {
      const citedIdxs = extractNumberedRefIds(narrativeBlob);

      for (const idx of citedIdxs) {
        const ref = report.references[idx - 1];
        if (!ref) continue; // [Ref 99] when references.length < 99 → ignored
        const docId = resolveReferencedDocumentId(ref, sed);
        if (docId) out.add(docId);
      }
    }
  }

  // Path B — ReportClaim.sources `doc:<id>`.
  for (const claim of report.claims ?? []) {
    for (const src of claim.sources ?? []) {
      const parsed = parseClaimSource(src);
      // parseClaimSource stores the prefix WITH the trailing colon
      // (e.g. 'doc:', not 'doc'). Match against the full token.
      if (parsed?.prefix === 'doc:' && parsed.payload) {
        out.add(parsed.payload);
      }
    }
  }

  // Path C — (per "filename") inline citations.
  if (liveDocs.length > 0) {
    const narrativeBlob = collectNarrativeBlob(report);
    if (narrativeBlob.length > 0) {
      PER_FILENAME_RE.lastIndex = 0;
      let fm: RegExpExecArray | null;
      while ((fm = PER_FILENAME_RE.exec(narrativeBlob))) {
        const fn = fm[1].trim();
        const match = matchDocByFilename(fn, liveDocs);
        if (match) out.add(match.id);
      }
    }
  }

  return out;
}

/**
 * Collect the report's narrative text into one blob for the `[Ref N]`
 * regex walk. Mirrors the strategy `buildCitedInMap` uses: iterate the
 * report-type's section list and pull each section's text through the
 * same shape-tolerant extractor. We can't enumerate every report
 * shape statically — feasibility, environmental, test-fit etc. each
 * carry their narrative on different typed fields.
 */
export function collectNarrativeBlob(report: SiteRagReport): string {
  const parts: string[] = [];
  const sections = getSectionsForType(report.reportType);
  for (const section of sections) {
    const text = extractSectionText(report, section);
    if (text) parts.push(text);
  }
  // Legacy saved reports retain a narrative summary outside the current
  // report-type section list. Include that known prose in both document
  // lineage and reference counters, while leaving metadata outside the walk.
  if (!sections.some(section => section.sectionId === 'summary') && typeof report.summary === 'string') {
    parts.push(report.summary);
  }
  return parts.join('\n');
}

/**
 * Pull narrative text for one section out of the report. Conservative
 * by design: an unknown section id returns ''. Mirrors the logic in
 * `buildCitedInMap` (`referenceBackrefs.ts`) — we use the same shape
 * probing so Path A's hit set stays consistent with the back-ref
 * `Cited in §X` strip the appendix renders above.
 */
function extractSectionText(report: SiteRagReport, section: SectionDef): string {
  const sid = section.sectionId;

  // The BTM section renders both its overview and typed option rows. Keep
  // citations in option reasons/actions even when the overview is a string.
  if (sid === 'btmOverview') {
    const overview = (report as unknown as Record<string, unknown>)['btmOverview'];
    return [typeof overview === 'string' ? overview : objJoin(overview), arrayJoin(report.btmOptions)].join(' ');
  }

  // Direct top-level string property (executiveSummary, sitingNotes, …).
  const direct = (report as unknown as Record<string, unknown>)[sid];
  if (typeof direct === 'string') return direct;

  // Walk known typed subtrees by section id. Conservative list —
  // adding a new section is opt-in.
  const subtrees: Record<string, () => string> = {
    projectIntentOverview: () => {
      const r = report as import('../types/siteRag').SiteRagReport;
      return r.projectIntentOverview ?? '';
    },
    keyFindings: () => arrayJoin((report as unknown as Record<string, unknown>)['keyFindings']),
    conclusion: () => objJoin(report.conclusion as unknown),
    riskAssessment: () => arrayJoin(report.risks as unknown),
    actionPlan: () => arrayJoin(report.recommendations as unknown),
    sitingNotes: () => arrayJoin((report as unknown as Record<string, unknown>)['sitingNotes']),
    gridStudy: () => objJoin((report as unknown as Record<string, unknown>)['gridStudy']),
    // These section ids render typed power/water fields rather than matching
    // top-level properties. Keep the shared citation projection on that same
    // basis; unrelated metadata and non-rendered analysis fields stay outside.
    gridCapacity: () => [report.powerAnalysis?.capacityAnalysis, report.powerAnalysis?.substationAssessment].filter(Boolean).join(' '),
    interconnectionQueue: () => report.powerAnalysis?.interconnectionQueue ?? '',
    rateStructures: () => report.powerAnalysis?.rateStructure ?? '',
    curtailmentRisk: () => report.powerAnalysis?.curtailmentRisk ?? '',
    renewableIntegration: () => report.powerAnalysis?.renewableIntegration ?? '',
    gridRedundancy: () => report.powerAnalysis?.redundancyAssessment ?? '',
    supplyAvailability: () => report.waterAnalysis?.supplyAvailability ?? '',
    groundwater: () => [report.waterAnalysis?.groundwaterAssessment, report.waterAnalysis?.surfaceWaterAssessment].filter(Boolean).join(' '),
    waterRights: () => report.waterAnalysis?.waterRightsAnalysis ?? '',
    droughtResilience: () => report.waterAnalysis?.droughtResilience ?? '',
    transportStudy: () => objJoin((report as unknown as Record<string, unknown>)['transportStudy']),
    connectivityStudy: () => objJoin((report as unknown as Record<string, unknown>)['connectivityStudy']),
    climateSevereWeather: () => objJoin((report as unknown as Record<string, unknown>)['climateSevereWeather']),
    extendedData: () => objJoin((report as unknown as Record<string, unknown>)['extendedData']),
    nepaScreening: () => objJoin((report as unknown as Record<string, unknown>)['nepaScreening']),
    developmentPathway: () => arrayJoin((report as unknown as Record<string, unknown>)['developmentPathway']),
    infrastructureInterconnection: () => objJoin((report as unknown as Record<string, unknown>)['infrastructureInterconnection']),
  };
  const pull = subtrees[sid];
  return pull ? pull() : '';
}

function arrayJoin(v: unknown): string {
  if (!Array.isArray(v)) return '';
  return v.map(item => {
    if (typeof item === 'string') return item;
    if (item && typeof item === 'object') {
      return Object.values(item as Record<string, unknown>)
        .filter(x => typeof x === 'string')
        .join(' ');
    }
    return '';
  }).join(' ');
}

function objJoin(v: unknown): string {
  if (!v || typeof v !== 'object') return '';
  if (Array.isArray(v)) return arrayJoin(v);
  return Object.values(v as Record<string, unknown>)
    .map(x => {
      if (typeof x === 'string') return x;
      if (Array.isArray(x)) return arrayJoin(x);
      if (x && typeof x === 'object') {
        return Object.values(x as Record<string, unknown>)
          .filter(y => typeof y === 'string')
          .join(' ');
      }
      return '';
    }).join(' ');
}
